/**
 * APX-382 — the vendor-domain fallback, shared with the edge proxy.
 *
 * `proxy.ts` has always resolved the portal as `hostname.includes(VENDOR_DOMAIN || 'vendors.')`,
 * so a missing or misconfigured `NEXT_PUBLIC_VENDOR_DOMAIN` still classified vendor
 * hosts correctly there. This module used to compare against the raw variable with
 * no fallback, so the two could disagree. That was harmless while both portals
 * fetched everything at boot; it stopped being harmless once the boot fan-out split
 * on this answer, because the client and the edge would then bootstrap different
 * portals for the same request.
 *
 * The three deciders — the edge (`proxy.ts`), the client (`checkVendorDomain`) and
 * the server-side Cognito config (`server/auth/cognito-config.ts`) — now all route
 * through `isVendorHost`, so "they agree" is a property of the code rather than of
 * three copies of a rule.
 */
export const VENDOR_DOMAIN_FALLBACK = 'vendors.';

export function getVendorDomain() {
  return process.env.NEXT_PUBLIC_VENDOR_DOMAIN;
}

export function getHostName() {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

/**
 * Trailing/leading dots and the `:port` a `Host:` header carries (but
 * `window.location.hostname` does not) are stripped so the edge and the client
 * compare the same string. Without the port strip, `vendors.lula.life:3000` would
 * fail an anchored comparison against `vendors.lula.life` that the old substring
 * match happened to pass.
 */
function normalizeHostPart(value: string): string {
  return value.trim().toLowerCase().split(':')[0].replace(/^\.+|\.+$/g, '');
}

/**
 * The pure decision, extracted so it can be tested over every host/config pair
 * without fighting jsdom's `[Unforgeable]` `window.location`.
 *
 * Matched on DNS LABEL boundaries, not as a bare substring. A plain
 * `hostname.includes(vendorDomain)` — which is what `proxy.ts` used to do, and
 * what this function did in the first cut of APX-382 — also answers `true` for any
 * host whose first label merely ENDS in the configured one:
 * `preferredvendors.lula.life` against `vendors.lula.life`. That is not academic
 * here: this predicate has ~20 consumers, and a coordinator host misread as a
 * vendor one selects the vendor Cognito pool (`AuthContext`), holds the vendor
 * onboarding gate (`PrivateLayout`) and flips the Header's portal switch — all
 * denials, none of them diagnosable from the UI.
 *
 * Vendor is therefore: the host IS the vendor domain, or it sits UNDER it
 * (`pr-123.vendors.lula.life` — the preview hosts the substring match was there to
 * cover). The bare `vendors` fallback, having no domain of its own, additionally
 * matches wherever it appears as a WHOLE label, which is how `vendors.<anything>`
 * still resolves with `NEXT_PUBLIC_VENDOR_DOMAIN` unset.
 */
export function isVendorHost(hostName: string, vendorDomain?: string): boolean {
  const host = normalizeHostPart(hostName);
  const domain = normalizeHostPart(vendorDomain || VENDOR_DOMAIN_FALLBACK);
  if (!host || !domain) return false;
  if (host === domain || host.endsWith(`.${domain}`)) return true;
  // Only for a single-label needle. Allowing a prefix match on a FULL domain would
  // hand the classification to whoever controls the parent of a lookalike host
  // (`vendors.lula.life.example.com`), which is the same unanchored hole one level up.
  if (domain.includes('.')) return false;
  return host.startsWith(`${domain}.`) || host.includes(`.${domain}.`);
}

export function checkVendorDomain() {
  return isVendorHost(getHostName(), getVendorDomain());
}
